import html2canvas from 'html2canvas';
import { useState, useCallback, useEffect, useRef } from 'react';
import { TankplanReportData, ReportGenerationOptions, ReportSubType } from 'src/utils/reportGenerator/ReportGeneratorTypes';
import { useStowageTankPlanStore } from 'src/context/StowageTankPlanContext';

export interface TankPlanReportGenerationData {
    reportSubType: ReportSubType;
    baseData: TankplanReportData;
    options: ReportGenerationOptions;
}

const CANVAS_SCALE_FACTOR = 2;
const JPEG_QUALITY = 0.1;
export const STABILITY_TIMEOUT = 300;
export const FALLBACK_PROCESS_DELAY = STABILITY_TIMEOUT + 1200; // 1.5s
const ELEMENT_POLL_INTERVAL = 100;
const ELEMENT_POLL_MAX_ATTEMPTS = 50; // 5 seconds max
const TANK_PLAN_CONTAINER_ID = 'tankPlanPdfReportContainer';

/**
 * Custom hook for managing tank plan image generation with internal state
 */
export const useTankPlanImageGenerator = (
    updateProgressMessage: (reportSubType?: ReportSubType, index?: number, total?: number) => void,
    onError?: (message: string) => void
): {
    generateTankPlanReports: (tankPlanElement: HTMLElement, reports: TankPlanReportGenerationData[], totalNumberOfReports: number) => void;
    generatedReports: TankPlanReportGenerationData[];
    isGenerating: boolean;
} => {
    const { setReportTankPlanCondition } = useStowageTankPlanStore();
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatedReports, setGeneratedReports] = useState<TankPlanReportGenerationData[]>([]);

    // Use refs to track current generation state to avoid closure issues
    const reportsRef = useRef<TankPlanReportGenerationData[]>([]);
    const currentIndexRef = useRef(0);
    const totalReportNumberRef = useRef(0);
    const tankPlanElementRef = useRef<HTMLElement | null>(null);
    const observerRef = useRef<MutationObserver | null>(null);
    const timeoutRef = useRef<number | null>(null);
    const ensureProcessingTimeoutRef = useRef<number | null>(null);

    const cleanup = useCallback(() => {
        if (timeoutRef.current !== null) {
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        if (ensureProcessingTimeoutRef.current !== null) {
            window.clearTimeout(ensureProcessingTimeoutRef.current);
            ensureProcessingTimeoutRef.current = null;
        }
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }
    }, []);

    const captureTankPlanImage = async (element: HTMLElement): Promise<HTMLCanvasElement | null> => {
        try {
            return await html2canvas(element, { scale: CANVAS_SCALE_FACTOR });
        } catch (error) {
            console.error('Failed to capture tank plan image:', error);
            return null;
        }
    };

    const processNextReport = useCallback(async (): Promise<void> => {
        // Re-acquire element from DOM to handle lazy-loaded components
        const element = tankPlanElementRef.current ?? document.getElementById(TANK_PLAN_CONTAINER_ID);
        if (element) {
            tankPlanElementRef.current = element;
        }
        const reports = reportsRef.current;
        const currentIndex = currentIndexRef.current;

        // Clear any potentially running fallback timer for this specific processing step,
        // as processNextReport is now actively handling it.
        if (ensureProcessingTimeoutRef.current !== null) {
            window.clearTimeout(ensureProcessingTimeoutRef.current);
            ensureProcessingTimeoutRef.current = null;
        }

        if (!element || currentIndex >= reports.length) {
            cleanup();
            setIsGenerating(false);
            return;
        }

        try {
            const image = await captureTankPlanImage(element);

            if (!image) {
                onError?.('Failed to capture tank plan image. Please try again.');
            } else if (image) {
                const base64Image = image.toDataURL('image/jpeg', JPEG_QUALITY);

                const newReport = {
                    reportSubType: reports[currentIndex].reportSubType,
                    baseData: {
                        ...reports[currentIndex].baseData,
                        imgData: base64Image,
                        imgWidth: image.width,
                        imgHeight: image.height,
                    },
                    options: reports[currentIndex].options,
                };

                setGeneratedReports((prev) => [...prev, newReport]);
                updateProgressMessage(reports[currentIndex].reportSubType, currentIndex + 1, totalReportNumberRef.current);
            }

            // Move to next report
            currentIndexRef.current = currentIndex + 1;

            // If there are more reports, set the next condition and wait for re-render
            if (currentIndex + 1 < reports.length) {
                const nextReport = reports[currentIndex + 1];
                setReportTankPlanCondition(nextReport.reportSubType.tankPlanCondition);

                // Set up a fallback timer to ensure the next report (at new currentIndexRef.current)
                // is processed if the MutationObserver doesn't detect changes for it.
                // This will be cleared by the observer if a mutation *is* detected.
                ensureProcessingTimeoutRef.current = window.setTimeout(() => {
                    console.warn(
                        `Fallback: Triggering processing for report index ${currentIndexRef.current} due to no mutation after condition set.`
                    );
                    processNextReport();
                }, FALLBACK_PROCESS_DELAY);
            } else {
                // We're done with all reports
                cleanup();
                setIsGenerating(false);
            }
        } catch (error) {
            console.error('Error processing report:', error);
            onError?.(`Error generating tank plan report: ${error instanceof Error ? error.message : 'Unknown error'}`);
            cleanup();
            setIsGenerating(false);
        }
    }, [cleanup, setReportTankPlanCondition, updateProgressMessage]);

    const setupObserver = useCallback(() => {
        // Clean up any existing observer
        cleanup(); // This will also clear ensureProcessingTimeoutRef

        // Re-acquire element from DOM to handle lazy-loaded components
        if (!tankPlanElementRef.current) {
            const el = document.getElementById(TANK_PLAN_CONTAINER_ID);
            if (el) {
                tankPlanElementRef.current = el;
            }
        }

        if (!tankPlanElementRef.current) return;

        // Create a new observer to detect when the tank plan has stabilized
        const observer = new MutationObserver(() => {
            // Clear any existing timer for stability
            if (timeoutRef.current !== null) {
                window.clearTimeout(timeoutRef.current);
            }
            // A mutation was detected, so clear the fallback timer as the observer is handling it.
            if (ensureProcessingTimeoutRef.current !== null) {
                window.clearTimeout(ensureProcessingTimeoutRef.current);
                ensureProcessingTimeoutRef.current = null;
            }

            // Wait for stability before capturing
            timeoutRef.current = window.setTimeout(() => {
                processNextReport();
                timeoutRef.current = null;
            }, STABILITY_TIMEOUT);
        });

        observer.observe(tankPlanElementRef.current, {
            childList: true,
            subtree: true,
            attributes: true,
            characterData: true,
        });

        observerRef.current = observer;
    }, [cleanup, processNextReport]);

    const generateTankPlanReports = useCallback(
        (tankPlanElement: HTMLElement, reports: TankPlanReportGenerationData[], totalNumberOfReports: number) => {
            if (reports.length === 0) return;

            cleanup();
            setGeneratedReports([]);
            setIsGenerating(true);
            totalReportNumberRef.current = totalNumberOfReports;

            tankPlanElementRef.current = tankPlanElement;
            reportsRef.current = reports;
            currentIndexRef.current = 0;

            const firstReport = reports[0];
            setReportTankPlanCondition(firstReport.reportSubType.tankPlanCondition);
            updateProgressMessage(firstReport.reportSubType, 1, totalNumberOfReports);

            // Wait for the tank plan container element to appear in the DOM
            // (it's lazy-loaded and may not be available immediately)
            const waitForElementAndStart = (attempt = 0) => {
                const el = document.getElementById(TANK_PLAN_CONTAINER_ID);
                if (el) {
                    tankPlanElementRef.current = el;
                    // Re-setup observer now that the element is available
                    setupObserver();

                    // Kick off initial processing via fallback timer.
                    // The observer will handle it if mutations occur from the initial setReportTankPlanCondition.
                    if (ensureProcessingTimeoutRef.current !== null) {
                        window.clearTimeout(ensureProcessingTimeoutRef.current);
                    }
                    ensureProcessingTimeoutRef.current = window.setTimeout(() => {
                        if (currentIndexRef.current === 0 && reportsRef.current.length > 0) {
                            console.warn(
                                `Fallback: Triggering processing for initial report (index ${currentIndexRef.current}) due to no mutation after initial condition set.`
                            );
                            processNextReport();
                        }
                    }, FALLBACK_PROCESS_DELAY);
                } else if (attempt < ELEMENT_POLL_MAX_ATTEMPTS) {
                    // Element not yet in DOM (lazy component loading) — retry
                    window.setTimeout(() => waitForElementAndStart(attempt + 1), ELEMENT_POLL_INTERVAL);
                } else {
                    console.error('Tank plan report container element not found after waiting. Aborting report generation.');
                    onError?.('Tank plan report container not found. Please try again.');
                    cleanup();
                    setIsGenerating(false);
                }
            };

            waitForElementAndStart();
        },
        [cleanup, setupObserver, setReportTankPlanCondition, updateProgressMessage]
    );

    // Clean up on unmount
    useEffect(() => {
        return cleanup;
    }, [cleanup]);

    return {
        generateTankPlanReports,
        generatedReports,
        isGenerating,
    };
};
